/**
 * The most recent significant gusts (Kyle, 2026-10-02): "show the 5 most recent significant gusts … the
 * most recent will be 0% transparent, the next 4 more and more transparent … a time of the gust, in
 * mins:secs ago format."
 *
 * What counts, and why:
 * - **A real gust:** the gust is at least `minSpreadKt` above the steady wind at the same reading. This is
 *   the "gust spread" a jumper cares about, not just a high number on a windy day. (Airport reports only
 *   carry a gust group when peaks run 10 kt or more above the lulls, so every reported airport gust counts.)
 * - **A peak, not a gusty spell:** a reading is a peak when no other reading within `windowMs` either side
 *   has a higher gust. One gusty spell of several minutes becomes one entry, its highest, instead of five
 *   near-copies. On sparse sources (an airport reporting every 20 minutes) every gusty report stands alone.
 * - Ties within a window keep the latest reading.
 *
 * The time is the reading's time. For the hangar station that is the end of the minute summary the gust
 * was in (the bridge keeps the minute's highest gust, not the second it happened), so "3:12 ago" is
 * accurate to within that minute.
 */

import type { Observation } from '@skyweather/weather-core'

export interface PeakGust {
  gustKt: number
  windKt: number | null
  at: number
}

export interface PeakGustOptions {
  /** How many to return, newest first. */
  max?: number
  /** A reading is a peak if nothing within this many ms either side gusted higher. */
  windowMs?: number
  /** Minimum gust minus steady wind, kt. */
  minSpreadKt?: number
}

export const PEAK_GUST_DEFAULTS = { max: 5, windowMs: 2 * 60_000, minSpreadKt: 5 } as const

export function recentPeakGusts(readings: Observation[], options: PeakGustOptions = {}): PeakGust[] {
  const { max, windowMs, minSpreadKt } = { ...PEAK_GUST_DEFAULTS, ...options }
  const gusty = readings
    .filter((o): o is Observation & { gustKt: number } => o.gustKt !== null && Number.isFinite(o.gustKt))
    .filter((o) => o.windKt === null || o.gustKt - o.windKt >= minSpreadKt)
    .sort((a, b) => a.observedAt - b.observedAt)
  // Compare against every reading with a gust, not only the significant ones, so a spell's peak is
  // judged against its own neighbourhood.
  const all = readings.filter((o) => o.gustKt !== null).sort((a, b) => a.observedAt - b.observedAt)
  const peaks = gusty.filter((o) =>
    all.every((n) => {
      if (n === o || Math.abs(n.observedAt - o.observedAt) > windowMs) return true
      const g = n.gustKt as number
      // A higher gust nearby beats this one; an equal one only if it is later (ties keep the latest).
      return g < o.gustKt || (g === o.gustKt && n.observedAt < o.observedAt)
    }),
  )
  return peaks
    .slice(-max)
    .reverse()
    .map((o) => ({ gustKt: o.gustKt, windKt: o.windKt, at: o.observedAt }))
}

/** "3:07 ago". Minutes are not capped at 59: "74:05 ago" reads plainly on a hangar screen. */
export function minsSecsAgo(at: number, now: number): string {
  const s = Math.max(0, Math.floor((now - at) / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} ago`
}

/** Opacity for the n-th newest (0-based): 1, 0.75, 0.55, 0.4, 0.28. */
export function gustOpacity(index: number): number {
  return [1, 0.75, 0.55, 0.4, 0.28][index] ?? 0.2
}
